import { useState, useRef, useEffect, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth, isGlobalAdmin, UserRole } from '../hooks/useAuth';
import { toast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

// --- Interfaces ---

interface User {
  userId: string;
  email: string;
  name: string;
  role: UserRole;
  inviteStatus: 'pending' | 'accepted';
  invitedAt?: string;
  createdAt: string;
}

interface ProjectMembership {
  projectId: string;
  shortName: string;
  description?: string;
  role: string;
  accessLevel: string;
}

interface TeamMembership {
  teamId: string;
  projectId: string;
  projectName: string;
  name: string;
  description?: string;
  role: string;
  accessLevel: string;
}

interface UserMemberships {
  projects: ProjectMembership[];
  teams: TeamMembership[];
}

interface Project {
  projectId: string;
  shortName: string;
}

interface Team {
  teamId: string;
  projectId: string;
  name: string;
}

const PROJECT_ROLES = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'scrum_master', label: 'Scrum Master' },
  { value: 'member', label: 'Member' },
];

const TEAM_ROLES = [
  { value: 'po', label: 'Product Owner' },
  { value: 'lead_dev', label: 'Lead Developer' },
  { value: 'developer', label: 'Developer' },
  { value: 'qa', label: 'QA Engineer' },
  { value: 'tester', label: 'Tester' },
  { value: 'devops', label: 'DevOps' },
  { value: 'other', label: 'Other' },
];

const ACCESS_LEVELS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

type SettingsTab = 'users' | 'presentation';

export default function Settings() {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as SettingsTab) || 'users';

  const setActiveTab = (tab: SettingsTab) => {
    setSearchParams({ tab });
  };

  if (!isGlobalAdmin(currentUser)) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">Only admins can access settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage users, presentation settings, and platform configuration.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-500">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Users
            </span>
          </button>
          <button
            onClick={() => setActiveTab('presentation')}
            className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'presentation'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Presentation
            </span>
          </button>
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'presentation' && <PresentationTab />}
    </div>
  );
}

// =============================================================================
// Users Tab (moved from Users.tsx)
// =============================================================================

function UsersTab() {
  const api = useApi();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { user: currentUser } = useAuth();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ items: User[] }>('/api/users'),
    enabled: isGlobalAdmin(currentUser),
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { name?: string; role?: UserRole } }) =>
      api.put(`/api/users/${userId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
      toast.success('User updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update user');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete user');
    },
  });

  const inviteUserMutation = useMutation({
    mutationFn: (data: { email: string; name?: string; role?: UserRole }) =>
      api.post('/api/auth/invite', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowInviteModal(false);
      toast.success('Invitation sent successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send invitation');
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (email: string) => api.post('/api/auth/resend-invite', { email }),
    onSuccess: () => {
      toast.success('Invitation resent successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to resend invitation');
    },
  });

  const handleDeleteUser = async (user: User) => {
    if (user.userId === currentUser?.userId) {
      toast.error('You cannot delete your own account');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete User',
      message: `Are you sure you want to delete "${user.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      deleteUserMutation.mutate(user.userId);
    }
  };

  const handleChangeUserRole = async (user: User, newRole: UserRole) => {
    if (user.userId === currentUser?.userId) {
      toast.error('You cannot change your own role');
      return;
    }

    const roleLabels: Record<UserRole, string> = {
      superadmin: 'Super Admin',
      admin: 'Admin',
      user: 'User',
    };

    const confirmed = await confirm({
      title: 'Change User Role',
      message: `Are you sure you want to change "${user.name}" from ${roleLabels[user.role]} to ${roleLabels[newRole]}?`,
      confirmText: 'Change Role',
      variant: newRole === 'user' ? 'danger' : 'info',
    });

    if (confirmed) {
      updateUserMutation.mutate({
        userId: user.userId,
        data: { role: newRole },
      });
    }
  };

  const handleResendInvite = async (user: User) => {
    const confirmed = await confirm({
      title: 'Resend Invitation',
      message: `Send a new invitation email to ${user.email}?`,
      confirmText: 'Resend',
      variant: 'info',
    });

    if (confirmed) {
      resendInviteMutation.mutate(user.email);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-primary-400">Failed to load users</p>
      </div>
    );
  }

  // Sort users with current user first
  const users = [...(data?.items || [])].sort((a, b) => {
    if (a.userId === currentUser?.userId) return -1;
    if (b.userId === currentUser?.userId) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 dark:text-gray-400">
            Manage platform users. Invite new users to grant them access.
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Invite User
        </button>
      </div>

      {/* Info card */}
      <div className="card bg-accent-blue/20 border-gray-200 dark:border-dark-500">
        <div className="flex gap-3">
          <div className="text-blue-500">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-sm text-accent-blue">
            <p className="font-medium">How user registration works</p>
            <p className="mt-1">
              Users must be invited by a Super Admin to access the platform.
              After receiving an invitation email, they can set their password and sign in.
              Click on a user row to see their project and team assignments.
            </p>
          </div>
        </div>
      </div>

      {/* Users table */}
      <div className="card">
        {users.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">No users yet. Invite someone to get started.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-500">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 w-8"></th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Assignments</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Joined</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isCurrentUser = user.userId === currentUser?.userId;
                  const isPending = user.inviteStatus === 'pending';
                  const isExpanded = expandedUserId === user.userId;
                  return (
                    <Fragment key={user.userId}>
                      <tr
                        className={`border-b border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600 cursor-pointer ${isExpanded ? 'bg-gray-50 dark:bg-dark-600' : ''}`}
                        onClick={() => setExpandedUserId(isExpanded ? null : user.userId)}
                      >
                        <td className="py-4 px-4">
                          <svg
                            className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm ${
                              isPending
                                ? 'bg-gray-100 dark:bg-dark-600 text-gray-500 dark:text-gray-400'
                                : 'bg-secondary-100 text-secondary-800'
                            }`}>
                              {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${isPending ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                  {user.name}
                                </span>
                                {isCurrentUser && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                    You
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {isPending ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-full border border-amber-200">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-full border border-green-200">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                              Active
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {user.role === 'superadmin' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              Super Admin
                            </span>
                          ) : user.role === 'admin' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-dark-600 text-gray-500 dark:text-gray-400 rounded-full">
                              User
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <UserAssignmentsSummary userId={user.userId} userRole={user.role} />
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                          <UserActions
                            user={user}
                            currentUser={currentUser}
                            isCurrentUser={isCurrentUser}
                            isPending={isPending}
                            onEdit={() => setEditingUser(user)}
                            onResendInvite={() => handleResendInvite(user)}
                            onChangeRole={(newRole) => handleChangeUserRole(user, newRole)}
                            onDelete={() => handleDeleteUser(user)}
                            isResending={resendInviteMutation.isPending}
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${user.userId}-expanded`}>
                          <td colSpan={7} className="bg-gray-50 dark:bg-dark-600 px-4 py-4 border-b border-gray-100 dark:border-dark-600">
                            <UserMembershipsDetail userId={user.userId} userRole={user.role} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit user modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          currentUserId={currentUser?.userId}
          currentUserRole={currentUser?.role}
          onClose={() => setEditingUser(null)}
          onSave={(data) =>
            updateUserMutation.mutate({ userId: editingUser.userId, data })
          }
          isSaving={updateUserMutation.isPending}
        />
      )}

      {/* Invite user modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={(data) => inviteUserMutation.mutate(data)}
          isInviting={inviteUserMutation.isPending}
          currentUserRole={currentUser?.role}
        />
      )}
    </div>
  );
}

// =============================================================================
// Presentation Tab
// =============================================================================

interface PresentationToken {
  tokenId: string;
  name: string;
  createdBy: string;
  createdAt: string;
  lastUsedAt?: string;
}

function PresentationTab() {
  const api = useApi();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [presentationTheme, setPresentationTheme] = useState(() =>
    localStorage.getItem('presentation-theme') || 'dark'
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdToken, setCreatedToken] = useState<{ name: string; token: string } | null>(null);

  const handleThemeChange = (theme: string) => {
    setPresentationTheme(theme);
    localStorage.setItem('presentation-theme', theme);
    toast.success('Presentation theme updated');
  };

  const { data: tokensData, isLoading: tokensLoading } = useQuery({
    queryKey: ['presentation-tokens'],
    queryFn: () => api.get<{ items: PresentationToken[] }>('/api/presentation-tokens'),
  });

  const createTokenMutation = useMutation({
    mutationFn: (name: string) =>
      api.post<{ tokenId: string; name: string; token: string }>('/api/presentation-tokens', { name }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['presentation-tokens'] });
      setShowCreateModal(false);
      setCreatedToken({ name: data.name, token: data.token });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create token');
    },
  });

  const deleteTokenMutation = useMutation({
    mutationFn: (tokenId: string) => api.delete(`/api/presentation-tokens/${tokenId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presentation-tokens'] });
      toast.success('Token deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete token');
    },
  });

  const handleDeleteToken = async (token: PresentationToken) => {
    const confirmed = await confirm({
      title: 'Delete Token',
      message: `Are you sure you want to delete the token "${token.name}"? Any displays using this token will lose access.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      deleteTokenMutation.mutate(token.tokenId);
    }
  };

  const tokens = tokensData?.items || [];

  return (
    <div className="space-y-6">
      {/* Theme setting */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Presentation Theme</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Choose the visual theme for the presentation mode display.
        </p>
        <select
          value={presentationTheme}
          onChange={(e) => handleThemeChange(e.target.value)}
          className="input w-48"
        >
          <option value="dark">Dark (default)</option>
          <option value="light">Light</option>
        </select>
      </div>

      {/* Technical Tokens */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Technical Tokens</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create static tokens for unattended displays (e.g., lobby TVs, wall monitors).
              Tokens allow access to the presentation mode without a user login.
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Token
          </button>
        </div>

        {tokensLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : tokens.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 rounded-lg border border-dashed border-gray-300 dark:border-dark-500">
            <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <p className="text-sm">No tokens created yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tokens.map((token) => (
              <div
                key={token.tokenId}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-500"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{token.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Created {new Date(token.createdAt).toLocaleDateString()}
                      {token.lastUsedAt && (
                        <> &middot; Last used {new Date(token.lastUsedAt).toLocaleDateString()}</>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteToken(token)}
                  disabled={deleteTokenMutation.isPending}
                  className="p-2 text-gray-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
                  title="Delete token"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick link */}
      <div className="card bg-dark-700/50 border-dark-600">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">Launch Presentation Mode</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Open the full-screen kiosk presentation. Press Escape to exit.
            </p>
          </div>
          <Link
            to="/presentation"
            className="btn-primary flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Launch
          </Link>
        </div>
      </div>

      {/* Create token modal */}
      {showCreateModal && (
        <CreateTokenModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(name) => createTokenMutation.mutate(name)}
          isCreating={createTokenMutation.isPending}
        />
      )}

      {/* Token created modal (shows token once) */}
      {createdToken && (
        <TokenCreatedModal
          name={createdToken.name}
          token={createdToken.token}
          onClose={() => setCreatedToken(null)}
        />
      )}
    </div>
  );
}

function CreateTokenModal({
  onClose,
  onCreate,
  isCreating,
}: {
  onClose: () => void;
  onCreate: (name: string) => void;
  isCreating: boolean;
}) {
  const [name, setName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed) {
      onCreate(trimmed);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create Technical Token</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-400 rounded"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div>
            <label className="label">Token Name</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., lobby-tv, conference-room"
              required
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              A descriptive name to identify where this token is used.
            </p>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-500">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isCreating || !name.trim()}>
              {isCreating ? 'Creating...' : 'Create Token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TokenCreatedModal({
  name,
  token,
  onClose,
}: {
  name: string;
  token: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS contexts
      const textArea = document.createElement('textarea');
      textArea.value = token;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-lg w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Token Created</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{name}</p>
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Copy this token now. It will not be shown again.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-dark-700 border border-gray-200 dark:border-dark-500 rounded-lg text-sm font-mono text-gray-900 dark:text-white break-all select-all">
            {token}
          </code>
          <button
            onClick={handleCopy}
            className="flex-shrink-0 p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            title="Copy to clipboard"
          >
            {copied ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        </div>

        <div className="flex justify-end">
          <button onClick={onClose} className="btn-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components (moved from Users.tsx)
// =============================================================================

function UserAssignmentsSummary({ userId, userRole }: { userId: string; userRole: string }) {
  const api = useApi();
  const isAdmin = userRole === 'superadmin' || userRole === 'admin';

  const { data, isLoading } = useQuery({
    queryKey: ['user-memberships', userId],
    queryFn: () => api.get<UserMemberships>(`/api/users/${userId}/memberships`),
    enabled: !isAdmin,
  });

  if (isAdmin) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 rounded">
        Full access
      </span>
    );
  }

  if (isLoading) {
    return <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>;
  }

  const projectCount = data?.projects?.length || 0;
  const teamCount = data?.teams?.length || 0;

  if (projectCount === 0 && teamCount === 0) {
    return <span className="text-sm text-gray-500 dark:text-gray-400">No assignments</span>;
  }

  return (
    <div className="flex gap-2">
      {projectCount > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-accent-blue/20 text-accent-blue rounded border border-blue-200">
          {projectCount} project{projectCount !== 1 ? 's' : ''}
        </span>
      )}
      {teamCount > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 rounded border border-purple-200">
          {teamCount} team{teamCount !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

function UserMembershipsDetail({ userId, userRole }: { userId: string; userRole: string }) {
  const api = useApi();
  const isAdmin = userRole === 'superadmin' || userRole === 'admin';

  const { data, isLoading } = useQuery({
    queryKey: ['user-memberships', userId],
    queryFn: () => api.get<UserMemberships>(`/api/users/${userId}/memberships`),
    enabled: !isAdmin,
  });

  if (isAdmin) {
    return (
      <div className="text-center py-4 text-amber-700 dark:text-amber-400">
        <p className="font-medium">Full access to all projects and teams</p>
        <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">Global admins have implicit access and do not need explicit assignments</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  const projects = data?.projects || [];
  const teams = data?.teams || [];

  if (projects.length === 0 && teams.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        <p>No project or team assignments</p>
        <p className="text-sm mt-1">Add this user to projects or teams from the project/team detail pages</p>
      </div>
    );
  }

  const getRoleLabel = (role: string, type: 'project' | 'team') => {
    const roles = type === 'project' ? PROJECT_ROLES : TEAM_ROLES;
    return roles.find(r => r.value === role)?.label || role;
  };

  const getAccessLevelLabel = (level: string) => {
    return ACCESS_LEVELS.find(l => l.value === level)?.label || level;
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Projects */}
      <div>
        <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          Projects ({projects.length})
        </h4>
        {projects.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Not assigned to any projects</p>
        ) : (
          <div className="space-y-2">
            {projects.map((p) => (
              <Link
                key={p.projectId}
                to={`/projects/${p.projectId}`}
                className="block p-2 bg-white dark:bg-dark-700 rounded border border-gray-200 dark:border-dark-500 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">{p.shortName}</span>
                  <div className="flex gap-1">
                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                      {getRoleLabel(p.role, 'project')}
                    </span>
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      p.accessLevel === 'admin' ? 'bg-primary-500/20 text-primary-400' :
                      p.accessLevel === 'editor' ? 'bg-accent-blue/20 text-accent-blue' :
                      'bg-gray-100 dark:bg-dark-600 text-gray-500 dark:text-gray-400'
                    }`}>
                      {getAccessLevelLabel(p.accessLevel)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Teams */}
      <div>
        <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Teams ({teams.length})
        </h4>
        {teams.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Not assigned to any teams</p>
        ) : (
          <div className="space-y-2">
            {teams.map((t) => (
              <Link
                key={t.teamId}
                to={`/teams/${t.teamId}`}
                className="block p-2 bg-white dark:bg-dark-700 rounded border border-gray-200 dark:border-dark-500 hover:border-purple-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{t.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({t.projectName})</span>
                  </div>
                  <div className="flex gap-1">
                    <span className="px-1.5 py-0.5 text-xs bg-accent-turquoise/20 text-accent-turquoise rounded">
                      {getRoleLabel(t.role, 'team')}
                    </span>
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      t.accessLevel === 'admin' ? 'bg-primary-500/20 text-primary-400' :
                      t.accessLevel === 'editor' ? 'bg-accent-blue/20 text-accent-blue' :
                      'bg-gray-100 dark:bg-dark-600 text-gray-500 dark:text-gray-400'
                    }`}>
                      {getAccessLevelLabel(t.accessLevel)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserActions({
  user,
  currentUser,
  isCurrentUser,
  isPending,
  onEdit,
  onResendInvite,
  onChangeRole,
  onDelete,
  isResending,
}: {
  user: User;
  currentUser: { userId: string; role: UserRole } | null;
  isCurrentUser: boolean;
  isPending: boolean;
  onEdit: () => void;
  onResendInvite: () => void;
  onChangeRole: (newRole: UserRole) => void;
  onDelete: () => void;
  isResending: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    function handleScroll() {
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Calculate menu position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  // For current user, only show edit button
  if (isCurrentUser) {
    return (
      <div className="flex justify-end">
        <button
          onClick={onEdit}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-600 rounded-lg transition-colors"
          title="Edit"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-end">
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-600 rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>

        {isOpen && (
          <div
            ref={menuRef}
            className="fixed w-48 bg-white dark:bg-dark-700 rounded-lg shadow-lg border border-gray-200 dark:border-dark-500 py-1 z-50"
            style={{ top: menuPosition.top, right: menuPosition.right }}
          >
            {isPending ? (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onResendInvite();
                }}
                disabled={isResending}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-blue-600 hover:bg-accent-blue/20 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Resend Invitation
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onEdit();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600"
                >
                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit User
                </button>
                {/* Role change options */}
                {user.role !== 'superadmin' && currentUser?.role === 'superadmin' && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onChangeRole('superadmin');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-dark-600"
                  >
                    <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    <span className="text-primary-400">Make Super Admin</span>
                  </button>
                )}
                {user.role !== 'admin' && (user.role !== 'superadmin' || currentUser?.role === 'superadmin') && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onChangeRole('admin');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-dark-600"
                  >
                    <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    <span className="text-accent-blue">Make Admin</span>
                  </button>
                )}
                {user.role !== 'user' && (user.role !== 'superadmin' || currentUser?.role === 'superadmin') && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onChangeRole('user');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-dark-600"
                  >
                    <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <span className="text-orange-700">Demote to User</span>
                  </button>
                )}
              </>
            )}
            <div className="border-t border-gray-100 dark:border-dark-600 mt-1 pt-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onDelete();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-primary-400 hover:bg-primary-500/20"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete User
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  currentUserId,
  currentUserRole,
  onClose,
  onSave,
  isSaving,
}: {
  user: User;
  currentUserId?: string;
  currentUserRole?: UserRole;
  onClose: () => void;
  onSave: (data: { name?: string; role?: UserRole }) => void;
  isSaving: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<UserRole>(user.role);
  const [activeTab, setActiveTab] = useState<'info' | 'assignments'>('info');
  const isCurrentUser = user.userId === currentUserId;

  const isGlobalAdminUser = user.role === 'superadmin' || user.role === 'admin';

  const { data: membershipsData, isLoading: membershipsLoading } = useQuery({
    queryKey: ['user-memberships', user.userId],
    queryFn: () => api.get<UserMemberships>(`/api/users/${user.userId}/memberships`),
    enabled: !isGlobalAdminUser,
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ items: Project[] }>('/api/projects'),
  });

  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<{ items: Team[] }>('/api/teams'),
  });

  const addProjectMemberMutation = useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) =>
      api.post(`/api/projects/${projectId}/members`, { userId, role: 'member', accessLevel: 'viewer' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-memberships', user.userId] });
      toast.success('Added to project');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add to project');
    },
  });

  const removeProjectMemberMutation = useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) =>
      api.delete(`/api/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-memberships', user.userId] });
      toast.success('Removed from project');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove from project');
    },
  });

  const addTeamMemberMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.post(`/api/teams/${teamId}/members`, { userId, role: 'developer', accessLevel: 'viewer' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-memberships', user.userId] });
      toast.success('Added to team');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add to team');
    },
  });

  const removeTeamMemberMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.delete(`/api/teams/${teamId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-memberships', user.userId] });
      toast.success('Removed from team');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove from team');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: { name?: string; role?: UserRole } = {};
    if (name !== user.name) data.name = name;
    if (role !== user.role && !isCurrentUser) data.role = role;
    onSave(data);
  };

  const projects = projectsData?.items || [];
  const teams = teamsData?.items || [];
  const assignedProjectIds = new Set(membershipsData?.projects?.map(p => p.projectId) || []);
  const assignedTeamIds = new Set(membershipsData?.teams?.map(t => t.teamId) || []);
  const availableProjects = projects.filter(p => !assignedProjectIds.has(p.projectId));
  const availableTeams = teams.filter(t => !assignedTeamIds.has(t.teamId));

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit User</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-400 rounded"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-dark-500 mb-4">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'info'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            User Info
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'assignments'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Assignments
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {activeTab === 'info' ? (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="label">Name</label>
                  <input
                    type="text"
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    className="input bg-gray-100 dark:bg-dark-600"
                    value={user.email}
                    disabled
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Email cannot be changed</p>
                </div>
                {!isCurrentUser && (
                  <div>
                    <label className="label">Role</label>
                    <select
                      className="input"
                      value={role}
                      onChange={(e) => setRole(e.target.value as UserRole)}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      {currentUserRole === 'superadmin' && (
                        <option value="superadmin">Super Admin</option>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {role === 'superadmin' && 'Full access to everything, can manage users and PI dates'}
                      {role === 'admin' && 'Full access to projects/teams, can manage users (except superadmins) and PI dates'}
                      {role === 'user' && 'No default access, requires project or team membership'}
                    </p>
                  </div>
                )}
                {isCurrentUser && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-amber-800">
                      You cannot change your own role
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-500">
                <button type="button" onClick={onClose} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {isGlobalAdminUser ? (
                <div className="text-center py-8 text-amber-700 dark:text-amber-400">
                  <p className="font-medium">Full access to all projects and teams</p>
                  <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">Global admins have implicit access and do not need explicit assignments</p>
                </div>
              ) : membershipsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
                </div>
              ) : (
                <>
                  {/* Projects */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">Projects</h3>
                    <div className="space-y-2">
                      {membershipsData?.projects?.map((p) => (
                        <div
                          key={p.projectId}
                          className="flex items-center justify-between p-2 bg-accent-blue/20 rounded border border-blue-200"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">{p.shortName}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({PROJECT_ROLES.find(r => r.value === p.role)?.label || p.role})
                            </span>
                          </div>
                          <button
                            onClick={() => removeProjectMemberMutation.mutate({ projectId: p.projectId, userId: user.userId })}
                            disabled={removeProjectMemberMutation.isPending}
                            className="p-1 text-primary-400 hover:text-red-700 hover:bg-primary-500/20 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      {availableProjects.length > 0 && (
                        <div className="flex gap-2">
                          <select
                            className="input flex-1"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                addProjectMemberMutation.mutate({ projectId: e.target.value, userId: user.userId });
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">Add to project...</option>
                            {availableProjects.map((p) => (
                              <option key={p.projectId} value={p.projectId}>
                                {p.shortName}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {membershipsData?.projects?.length === 0 && availableProjects.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No projects available</p>
                      )}
                    </div>
                  </div>

                  {/* Teams */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">Teams</h3>
                    <div className="space-y-2">
                      {membershipsData?.teams?.map((t) => (
                        <div
                          key={t.teamId}
                          className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">{t.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">({t.projectName})</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              - {TEAM_ROLES.find(r => r.value === t.role)?.label || t.role}
                            </span>
                          </div>
                          <button
                            onClick={() => removeTeamMemberMutation.mutate({ teamId: t.teamId, userId: user.userId })}
                            disabled={removeTeamMemberMutation.isPending}
                            className="p-1 text-primary-400 hover:text-red-700 hover:bg-primary-500/20 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      {availableTeams.length > 0 && (
                        <div className="flex gap-2">
                          <select
                            className="input flex-1"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                addTeamMemberMutation.mutate({ teamId: e.target.value, userId: user.userId });
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">Add to team...</option>
                            {availableTeams.map((t) => {
                              const project = projects.find(p => p.projectId === t.projectId);
                              return (
                                <option key={t.teamId} value={t.teamId}>
                                  {t.name} ({project?.shortName || 'Unknown'})
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}
                      {membershipsData?.teams?.length === 0 && availableTeams.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No teams available</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-dark-500">
                <button type="button" onClick={onClose} className="btn-outline">
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InviteUserModal({
  onClose,
  onInvite,
  isInviting,
  currentUserRole,
}: {
  onClose: () => void;
  onInvite: (data: { email: string; name?: string; role?: UserRole }) => void;
  isInviting: boolean;
  currentUserRole?: UserRole;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('user');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onInvite({
      email: email.trim().toLowerCase(),
      name: name.trim() || undefined,
      role,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invite User</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-400 rounded"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
            </div>
            <div>
              <label className="label">Name (optional)</label>
              <input
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                If not provided, will be derived from email
              </p>
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                {currentUserRole === 'superadmin' && (
                  <option value="superadmin">Super Admin</option>
                )}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {role === 'superadmin' && 'Full access to everything, can manage users and PI dates'}
                {role === 'admin' && 'Full access to projects/teams, can manage users (except superadmins) and PI dates'}
                {role === 'user' && 'No default access, requires project or team membership'}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-500">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={isInviting}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {isInviting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
